import { LocalDataSource } from 'ng2-smart-table';
import { UserRolesService } from './../../../../shared/services/user-roles.service';
import { UserAuthService } from './../../../../shared/services/user-auth.service';
import { RealtimeTreatmentStorageService } from './../../../../shared/services/realtime-treatment-storage.service';
import { INTERVAL_CHECK_TREATMENT_DATA } from './../../../../shared/helpers/app.constants';
import { IntervalTimer } from './../../../../shared/helpers/intervalTimer';
import { Component, OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { Subscription, forkJoin, Subject, of, Observable } from 'rxjs';
import { TreatmentService } from '../../../../shared/services/treatment.service';
import { Helper } from '../../../../shared/helpers/helper';
import { forEach, isEmpty } from 'lodash';
import { Router } from '@angular/router';
import { CookieStorageService } from '../../../../core/services/cookie-storage.service';
import { DecimalPipe } from '@angular/common';
import { LoadingOverlayService } from '../../../../shared/services';
import { map, switchMap, finalize, tap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-realtime-list',
  templateUrl: './realtime-list.component.html',
  styleUrls: ['./realtime-list.component.scss']
})
export class RealtimeListComponent implements OnInit, OnDestroy {
  private decimalPipe = new DecimalPipe('en-US');
  subscriptions: Subscription[] = [];
  chooseGridView: boolean = true;
  gridView: any;
  editData: any;
  pagingState = {
    page: 1,
    pageSize: 18,
    collectionSize: 0
  };

  listWells: any[] = [];
  destroyTimer$: Subject<any> = new Subject();
  intervalTimer: IntervalTimer;
  currentRole: string;
  // well table config
  tableSettings = {
    actions: {
      add: false,
      edit: false,
      delete: false,
    },
    hideSubHeader: true,
    pager: {
      perPage: 24,
      display: false,
    },
    columns: {
      wellAPI: {
        title: 'Well API',
        type: 'string',
        width: '20%',
        sort: false,
      },
      wellName: {
        title: 'Well Name',
        type: 'string',
        width: '25%',
        sort: false,
      },
      serviceCompanyName: {
        title: 'Service Company',
        type: 'string',
        width: '20%',
        sort: false,
      },
      operatorCompanyName: {
        title: 'Operator',
        type: 'string',
        width: '20%',
        sort: false,
      },
      treatmentNumberName: {
        title: 'Treatment Number',
        type: 'string',
        width: '15%',
        sort: false,
      },
      // realTime: {
      //   title: 'Elapsed Time (min)',
      //   type: 'string',
      //   width: '15%',
      //   sort: false,
      // },
      // fracproTime: {
      //   title: 'Model Time (min)',
      //   type: 'string',
      //   width: '15%',
      //   sort: false,
      // },
    },
  };

  dataSource: LocalDataSource = new LocalDataSource();

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private service: TreatmentService,
    private userAuthService: UserAuthService,
    private userRolesService: UserRolesService,
    private cookieService: CookieStorageService,
    private loadingOverlayService: LoadingOverlayService,
    private realtimeTreatmentStorageService: RealtimeTreatmentStorageService
  ) {
    const userInfo = this.userAuthService.getUserInfo();
    const userRole = this.userRolesService.toUserRole(userInfo);
    if (userRole.toLowerCase() === 'field engineer') {
      this.currentRole = userRole;
    }

    const gridViewCookies = this.cookieService.getCookies('gridView');
    if (gridViewCookies) {
      this.gridView = JSON.parse(gridViewCookies);
      this.chooseGridView = typeof this.gridView === 'object' ? !!this.gridView.realtimeTreatment : !!this.gridView;
    } else {
      this.setGridViewCookie();
    }
  }

  ngOnInit() {
    this.loadingOverlayService.show();
    // first load items
    this.getListRealtimeItemsPaging(this.pagingState.page, this.pagingState.pageSize);
    // real-time
    // this.runIntervalTimer();
  }

  ngOnDestroy(): void {
    this.destroyTimer$.next(true);
    this.destroyTimer$.complete();
    this.onDestroyInterval();
    this.realtimeTreatmentStorageService.removeRealtimeTreatmentsData();
    Helper.unsubscribe(this.subscriptions);
  }

  @HostListener('window:visibilitychange', ['$event'])
  onVisibilityChange(event) {
    if (document.hidden === true && document.visibilityState === 'hidden') {
      this.onUpdateInterval(false);
    } else if (document.hidden === false && document.visibilityState === 'visible') {
      this.onUpdateInterval(true);
    }
  }

  private onUpdateInterval(resume = true): void {
    if (this.intervalTimer) {
      if (!resume) {
        this.intervalTimer.pause();
      } else {
        this.intervalTimer.resume();
      }
    }
  }

  private onDestroyInterval(): void {
    if (this.intervalTimer) {
      this.intervalTimer.stop();
    }
  }

  private runIntervalTimer() {
    this.ngZone.runOutsideAngular(() => {
      this.intervalTimer = new IntervalTimer(
        () => {
          this.getListRealtimeItemsPaging(this.pagingState.page, this.pagingState.pageSize);
        }, INTERVAL_CHECK_TREATMENT_DATA
      );
    });
  }

  private getListRealtimeItemsPaging(page, pageSize): void {
    this.subscriptions.push(
      this.service.getTreatmentInProgressDataPaging(page, pageSize)
        .pipe(switchMap(response => {
          const listDetails = response.items;
          this.pagingState.collectionSize = response.totalCount;
          // check btn live
          if (page === 1) this.service.handleMenuRealtime(response.items);
          // cache treatment realtime
          this.realtimeTreatmentStorageService.setRealtimeTreatmentsData(listDetails);
          if (!listDetails || !listDetails.length) this.loadingOverlayService.hide();
          return of(listDetails);
        }))
        .pipe(map(items => {
          return items.map(item => {
            // convert sec to min
            item.fracproTime = item.fracproTime / 60;
            item.fracproTime = this.decimalPipe.transform(item.fracproTime, `1.0-1`);
            item.realTime = item.realTime / 60;
            item.realTime = this.decimalPipe.transform(item.realTime, `1.0-1`);
            item.treatmentNumberName = item.treatmentNumber > 0 ? item.treatmentNumber : 'Monitor';
            return item;
          });
        }))
        .pipe(finalize(() => this.loadingOverlayService.hide()))
        .pipe(takeUntil(this.destroyTimer$))
        .subscribe(items => {
          this.listWells = Helper.handleShowHighestByTreatmentId(items);
          this.dataSource.load(this.listWells);
        })
    );
  }

  chooseView(check: boolean) {
    this.chooseGridView = check;
    this.setGridViewCookie();
  }

  private setGridViewCookie(): void {
    if (!this.gridView || typeof this.gridView !== 'object') {
      this.gridView = {};
    }

    this.gridView.realtimeTreatment = this.chooseGridView;
    let expireInSeconds = this.cookieService.getCookies('expireInSeconds');
    expireInSeconds = expireInSeconds ? JSON.parse(expireInSeconds) : 1;
    // tslint:disable-next-line: radix
    this.cookieService.setCookies('gridView', JSON.stringify(this.gridView), parseInt(expireInSeconds));
  }

  editProblemWell(item: any) {
    this.editData = item;
    if (item.treatmentId) {
      this.router.navigate(['pages/treatments/realtime', item.treatmentId], {
        queryParams: { wellId: item.wellId }
      });
    }
  }

  pageChangeHandler(page) {
    this.pagingState.page = page;
    this.loadingOverlayService.show();

    // destroy previous timer
    this.destroyTimer$.next(true);
    this.getListRealtimeItemsPaging(this.pagingState.page, this.pagingState.pageSize);
  }

}
